import { useState, useMemo, type FormEvent } from 'react';
import { useSearchParams, Link } from 'react-router';
import { Eye, EyeOff, Check, X, CheckCircle } from 'lucide-react';
import api from '@/lib/api';

const rules = [
  { label: '8+ chars', test: (pw: string) => pw.length >= 8 },
  { label: 'Uppercase', test: (pw: string) => /[A-Z]/.test(pw) },
  { label: 'Lowercase', test: (pw: string) => /[a-z]/.test(pw) },
  { label: 'Number', test: (pw: string) => /\d/.test(pw) },
];

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const ruleResults = useMemo(
    () => rules.map((r) => ({ ...r, passed: r.test(password) })),
    [password],
  );
  const allPassed = ruleResults.every((r) => r.passed);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!allPassed || !token) return;
    setError('');
    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, password });
      setSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Reset failed. The link may have expired.');
    } finally {
      setLoading(false);
    }
  }

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="w-full max-w-sm text-center">
          <h1 className="text-xl font-extrabold mb-2">Invalid Link</h1>
          <p className="text-sm text-ink-3 mb-6">This password reset link is invalid or has expired.</p>
          <Link to="/forgot-password" className="text-sm text-orange font-semibold hover:underline">
            Request a new link
          </Link>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="w-full max-w-sm text-center">
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6"
            style={{ background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.2)' }}
          >
            <CheckCircle size={28} className="text-green" />
          </div>
          <h1 className="text-xl font-extrabold mb-2">Password Reset!</h1>
          <p className="text-sm text-ink-3 mb-6">Your password has been updated successfully.</p>
          <Link
            to="/login"
            className="inline-block px-6 py-2.5 rounded-lg text-sm font-bold text-white"
            style={{ background: 'linear-gradient(135deg, #f97316, #f59e0b)' }}
          >
            Sign In
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="w-full max-w-sm">
        <h1 className="text-xl font-extrabold mb-2">Set new password</h1>
        <p className="text-sm text-ink-3 mb-6">Choose a strong password for your account.</p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-[11px] font-semibold text-ink-3 mb-1.5">New Password</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter new password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoFocus
                className="w-full px-3 pr-10 py-2.5 text-sm font-mono rounded-lg bg-surface text-ink placeholder:text-ink-4 outline-none transition-all duration-200 border border-border focus:border-orange focus:shadow-[0_0_8px_rgba(249,115,22,0.15)]"
              />
              <button
                type="button"
                tabIndex={-1}
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-ink-4 hover:text-ink-3 transition-colors"
              >
                {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {ruleResults.map((r) => (
              <span
                key={r.label}
                className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-md transition-colors ${
                  r.passed ? 'bg-green-bg text-green' : 'bg-surface text-ink-4'
                }`}
              >
                {r.passed ? <Check size={10} /> : <X size={10} />}
                {r.label}
              </span>
            ))}
          </div>

          {error && <p className="text-xs text-red font-semibold">{error}</p>}

          <button
            type="submit"
            disabled={loading || !allPassed}
            className="w-full py-3 rounded-lg text-sm font-bold text-white transition-all hover:brightness-110 disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg, #f97316, #f59e0b)' }}
          >
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>
      </div>
    </div>
  );
}
